import { Component } from 'react';

// Valid object-form component types (memo / forwardRef / lazy are objects tagged
// with a $$typeof symbol). Used to validate the `fallback` prop without pulling in
// react-is. A React *element* (e.g. <X />) and plain objects are deliberately NOT
// in this set, so they're rejected and degrade to the inline notice rather than
// crashing the boundary's own render.
const FALLBACK_COMPONENT_TYPES = new Set([
  Symbol.for('react.memo'),
  Symbol.for('react.forward_ref'),
  Symbol.for('react.lazy'),
]);

function isRenderableComponent(c) {
  if (typeof c === 'function') return true; // plain function component
  return typeof c === 'object' && c !== null && FALLBACK_COMPONENT_TYPES.has(c.$$typeof);
}

// NOTE: This is the one sanctioned class component in frontend/src. React error
// boundaries REQUIRE a class — getDerivedStateFromError / componentDidCatch have
// no hook equivalent — so the "functional components only" rule does not apply
// here (documented as an exception in CLAUDE.md and .coderabbit.yaml). Preferred
// over pulling in react-error-boundary just to avoid a single class.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Gate the diagnostic to DEV: in production it would add console noise and
    // leak the React component stack (errorInfo.componentStack). Follows the
    // DEV-gated logging pattern used for diagnostics elsewhere (vbtipp/*,
    // ChatKitPanel, …). There is no production error sink wired up; the fallback
    // still renders regardless of this log (see getDerivedStateFromError). If a
    // sink is ever added this is the integration point — errorInfo.componentStack
    // is only available here.
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    // Recover automatically when the user navigates away from a crashed route.
    // App passes the current pathname as `resetKey`; when it changes WHILE we're
    // showing the fallback, clear the error so the new route renders. This is the
    // resetKeys pattern (not keying the subtree off pathname, which would remount
    // the whole routed tree on every navigation) — it only resets when actually
    // in an error state, so normal navigation is unaffected.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      // Guard the fallback prop: a non-component fallback would error inside THIS
      // boundary's own render, which React cannot catch here — it would propagate
      // up and, with no outer boundary, blank the whole app. Accept only genuine
      // component types (function, or memo/forwardRef/lazy objects); a React
      // element, plain object, or primitive degrades to a dependency-free inline
      // notice instead of crashing.
      // (Note: the wired ErrorFallback calls useTranslation, so the boundary must
      // stay inside LanguageProvider — see main.jsx.)
      if (!isRenderableComponent(FallbackComponent)) {
        // Intentionally minimal and NOT localized: this branch renders ONLY when
        // the `fallback` prop is missing / not a valid component type — a wiring
        // mistake the app never makes (App always passes ErrorFallback). Keeping it
        // a dependency-free literal means this guard branch can't itself throw. It
        // does NOT (and cannot) catch a *valid* fallback that throws during its own
        // render — React can't catch that within the same boundary; ErrorFallback
        // is localized and sits inside LanguageProvider, so that isn't a concern.
        return <div role="alert">Something went wrong. Please reload the page.</div>;
      }
      return <FallbackComponent error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
